import {
  calculateFinalTotal,
  calculateListSubtotal,
  getLineDiscountInfo,
  roundMoney,
} from "../../../utils/saleDiscountCalc";
import { humanizeSlug, resolveDisplayStepLabel } from "../../workflowDisplay";

const orderUnitPrice = (m) => m.modelId?.price || 0;

export const KDV_RATE = 0.2;

const STATUS_MAP = {
  pending: { label: "Yön. Kontrol Onayı", appearance: "warning-subtle" },
  admin1_approved: {
    label: "Müşteriye Sunulabilir",
    appearance: "info-subtle",
  },
  user_approved: {
    label: "Sipariş Onayı Bekliyor",
    appearance: "primary-subtle",
  },
  rejected: { label: "Reddedildi", appearance: "danger-subtle" },
  cancelled: { label: "İptal", appearance: "neutral-subtle" },
};

function formatStatusFallback(status) {
  if (status.endsWith("_rejected")) return "Reddedildi";
  if (status.endsWith("_approved")) {
    const base = String(status)
      .replace(/_approved$/i, "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\d+/g, "")
      .replace(/\s+/g, " ")
      .trim();
    // Turkish-ish common tokens
    const nicer = base
      .replace(/Siparis/gi, "Sipariş")
      .replace(/Onay\b/gi, "Onay")
      .replace(/Teklif/gi, "Teklif");
    return nicer ? `${nicer} — Onaylandı` : "Onaylandı";
  }
  return humanizeSlug(status);
}

/** @deprecated use getOrderStatusDisplay(order) for accepted states */
export const orderStatusConfig = STATUS_MAP;

export function hasOrderSale(order) {
  if (!order?.saleId) return false;
  const saleId = order.saleId;
  return typeof saleId === "string" ? !!saleId : !!saleId._id;
}

export function canMakeSaleFromOrder(order) {
  if (!order) return false;
  // Backend derives this from the active workflow's dynamic final step.
  if (typeof order.canMakeSale === "boolean") return order.canMakeSale;
  if (hasOrderSale(order)) return false;
  if (order.currentApproval?.status === "pending") return false;
  if (Array.isArray(order.workflowSteps) && order.workflowSteps.length) {
    const finalStep = [...order.workflowSteps].sort(
      (a, b) => (b.stepNumber || 0) - (a.stepNumber || 0),
    )[0];
    return (
      !!finalStep?.onApproveStatus && order.status === finalStep.onApproveStatus
    );
  }
  return order.status === "accepted";
}

/** accepted: iki ayrı rozet — sipariş bekliyor vs sipariş oluşturulmuş */
export function getOrderStatusDisplay(order, workflowSteps) {
  if (order?.statusDisplay?.label) {
    // Backend sometimes stores machine codes in label; never show raw.
    const label = formatStatusFallback(order.statusDisplay.label);
    const looksRaw =
      /_approved$/i.test(order.statusDisplay.label) ||
      /_rejected$/i.test(order.statusDisplay.label) ||
      /_/.test(order.statusDisplay.label);
    return looksRaw
      ? { ...order.statusDisplay, label }
      : order.statusDisplay;
  }
  if (!order?.status) {
    return { label: "-", appearance: "neutral-subtle" };
  }
  if (hasOrderSale(order)) {
    return { label: "Sipariş Oluşturuldu", appearance: "success-subtle" };
  }
  if (canMakeSaleFromOrder({ ...order, workflowSteps })) {
    return { label: "Sipariş Yapılabilir", appearance: "success-subtle" };
  }
  if (order.currentApproval?.status === "pending") {
    const pendingLabel = resolveDisplayStepLabel({
      stepLabel: order.currentApproval.stepLabel,
      workflowStepKey: order.currentApproval.workflowStepKey,
    });
    return {
      label: pendingLabel || formatStatusFallback(order.status),
      appearance:
        order.currentApproval.stage === "user"
          ? "info-subtle"
          : "warning-subtle",
    };
  }

  // Dynamic workflow check
  const dynamicStep = workflowSteps?.find(
    (s) =>
      s.stepKey === order.status ||
      s.onApproveStatus === order.status ||
      s.onRejectStatus === order.status,
  );

  if (dynamicStep) {
    if (dynamicStep.onRejectStatus === order.status) {
      let rejectLabel = "Reddedildi";
      if (
        dynamicStep.rejectModalTitle &&
        dynamicStep.rejectModalTitle !== "Reddet"
      ) {
        rejectLabel = dynamicStep.rejectModalTitle;
      }
      return { label: rejectLabel, appearance: "danger-subtle" };
    }
    if (dynamicStep.onApproveStatus === order.status) {
      const nextStep = workflowSteps.find(
        (s) => s.stepNumber === dynamicStep.stepNumber + 1,
      );
      if (nextStep) {
        const nextLabel = resolveDisplayStepLabel({
          stepLabel: nextStep.label,
          workflowStepKey: nextStep.stepKey,
        });
        return {
          label: `${nextLabel} Bekliyor`,
          appearance: "primary-subtle",
        };
      }
      return { label: "Sipariş Yapılabilir", appearance: "success-subtle" };
    }
    return {
      label:
        resolveDisplayStepLabel({
          stepLabel: dynamicStep.label,
          workflowStepKey: dynamicStep.stepKey,
        }) || formatStatusFallback(order.status),
      appearance: "warning-subtle",
    };
  }

  const cfg = STATUS_MAP[order.status];
  return cfg
    ? { label: cfg.label, appearance: cfg.appearance }
    : {
        label: formatStatusFallback(order.status),
        appearance: "neutral-subtle",
      };
}

export function inferOrderDiscountScope(order) {
  if (!order) return "per_line";
  if (
    order.discountScope === "order_total" ||
    order.discountScope === "per_line"
  ) {
    return order.discountScope;
  }
  const hasLine = (order.models || []).some(
    (m) => Number(m.discountPercent) > 0,
  );
  const hasHeader =
    Number(order.orderDiscountPercent) > 0 ||
    Number(order.orderDiscountFixed) > 0;
  if (hasHeader && !hasLine) return "order_total";
  return "per_line";
}

export function clampPctView(v) {
  return Math.min(100, Math.max(0, Number(v) || 0));
}

export function inferOrderHeaderKind(order) {
  if (Number(order?.orderDiscountPercent) > 0) return "percent";
  if (Number(order?.orderDiscountFixed) > 0) return "fixed";
  return "percent";
}

export function buildOrderDiscountOpts(order) {
  const models = order?.models || [];
  return {
    discountScope: inferOrderDiscountScope(order),
    orderHeaderKind: inferOrderHeaderKind(order),
    orderDiscountPercent: Number(order?.orderDiscountPercent) || 0,
    orderDiscountFixed: Number(order?.orderDiscountFixed) || 0,
    listSubtotal: calculateListSubtotal(models, orderUnitPrice),
  };
}

/** İskonto column: per-line % or general discount share per model. */
export function formatOrderLineDiscount(m, order, format) {
  const scope = inferOrderDiscountScope(order);
  if (scope === "per_line") {
    const pct = clampPctView(m.discountPercent);
    return pct > 0 ? `${pct}%` : "—";
  }
  const info = getLineDiscountInfo(
    m,
    buildOrderDiscountOpts(order),
    orderUnitPrice,
  );
  const kind = inferOrderHeaderKind(order);
  if (kind === "percent" && info.percent > 0) {
    return `%${clampPctView(info.percent)}`;
  }
  if (kind === "fixed" && info.amount > 0) {
    return `-${format(info.amount, "money")}`;
  }
  return "—";
}

export function orderLineDisplayAmount(m, order) {
  return getLineDiscountInfo(m, buildOrderDiscountOpts(order), orderUnitPrice)
    .net;
}

export function orderHasDiscountBlock(order) {
  if (!order) return false;
  if (
    Number(order.orderDiscountPercent) > 0 ||
    Number(order.orderDiscountFixed) > 0
  ) {
    return true;
  }
  return (order.models || []).some((m) => Number(m.discountPercent) > 0);
}

/**
 * Totals for detail views: list (no discount), net (after discount / ara toplam),
 * KDV %20, and grand total with KDV.
 */
export function computeOrderMoneyTotals(order) {
  const models = order?.models || [];
  const opts = buildOrderDiscountOpts(order);
  const listSubtotal = roundMoney(
    models.length
      ? calculateListSubtotal(models, orderUnitPrice)
      : Number(order?.totalAmount) || 0,
  );
  const netTotal = roundMoney(
    models.length
      ? calculateFinalTotal(models, opts, orderUnitPrice)
      : Number(order?.totalAmount) || 0,
  );
  const discountAmount = roundMoney(Math.max(0, listSubtotal - netTotal));
  const kdv = roundMoney(netTotal * KDV_RATE);
  const grandTotal = roundMoney(netTotal + kdv);
  return {
    listSubtotal,
    discountAmount,
    netTotal,
    kdv,
    grandTotal,
    hasDiscount: orderHasDiscountBlock(order) && discountAmount > 0,
    discountScope: inferOrderDiscountScope(order),
  };
}

/** Sale lines usually have no stored discounts — still show KDV breakdown. */
export function computeSaleMoneyTotals(sale) {
  const models = sale?.models || [];
  const listSubtotal = roundMoney(
    models.length
      ? models.reduce(
          (sum, m) => sum + (m.modelId?.price || 0) * (m.count || 0),
          0,
        )
      : Number(sale?.totalAmount) || 0,
  );
  const netTotal = roundMoney(
    models.length ? listSubtotal : Number(sale?.totalAmount) || 0,
  );
  const kdv = roundMoney(netTotal * KDV_RATE);
  const grandTotal = roundMoney(netTotal + kdv);
  return {
    listSubtotal,
    discountAmount: 0,
    netTotal,
    kdv,
    grandTotal,
    hasDiscount: false,
  };
}

export function getRejectReasonFromLogs(order) {
  const logs = order?.logs;
  if (!Array.isArray(logs) || logs.length === 0) return "";
  const rejectLog = [...logs]
    .reverse()
    .find((l) => (l?.description || "").toLowerCase().includes("reddedildi"));
  const desc = rejectLog?.description || "";
  const idx = desc.indexOf(":");
  return idx >= 0 ? desc.slice(idx + 1).trim() : "";
}

export function formatPaymentMethod(method) {
  if (method === "cash") return "Nakit";
  if (method === "credit_card") return "Kredi Kartı";
  if (method === "bank_transfer") return "Banka Transferi";
  if (method === "document") return "Senet/Belge";
  return method || "-";
}

export function resolveOrderFromItem(item) {
  return item?.entity ?? item;
}

export function resolveCompanyNameFromItem(item) {
  const summaryName = item?.summary?.companyName;
  if (summaryName && summaryName !== "-") return summaryName;
  const order = resolveOrderFromItem(item);
  return order?.companyId?.name || "-";
}

export function parseRateNum(val) {
  if (val === undefined || val === null || val === "") return undefined;
  const n = Number(String(val).trim().replace(",", "."));
  return !isNaN(n) && n > 0 ? n : undefined;
}

export function formatRateDisplay(rate) {
  if (!rate) return "-";
  return `${Number(rate).toLocaleString("tr-TR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })} TL`;
}
